import React, { useEffect, useRef, useState } from "react";
import { FiMic, FiMicOff, FiX } from "react-icons/fi";

export default function MessageInput({ onSend, isStreaming = false, onCancelStream }) {
  const [text, setText] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [isSpeechSupported, setIsSpeechSupported] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    setIsSpeechSupported(!!SpeechRecognition);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (text.trim()) {
      onSend(text);
      setText("");
    }
  };

  const startListening = () => {
    if (isListening) return;
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    try {
      const recognition = new SpeechRecognition();
      recognition.lang = 'en-US';
      recognition.continuous = false;
      recognition.interimResults = true;
      let finalTranscript = "";
      recognition.onstart = () => setIsListening(true);
      recognition.onerror = () => setIsListening(false);
      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; ++i) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += transcript;
          else interim += transcript;
        }
        // Show live interim text in the box
        setText(finalTranscript || interim);
      };
      recognition.onend = () => {
        setIsListening(false);
        const spoken = (finalTranscript || text || "").trim();
        if (spoken.length > 0) {
          // briefly show full text, then send
          setText(spoken);
          onSend(spoken);
          setText("");
        }
      };
      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      setIsListening(false);
    }
  };

  const stopListening = () => {
    try {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    } catch {}
  };

  return (
    <div className="px-6 py-4 border-t border-gray-200/40 dark:border-gray-700/30 bg-gray-50 dark:bg-gray-900 relative">
      {/* Subtle decorative accent */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-200 dark:via-indigo-800 to-transparent"></div>
      
      <form onSubmit={handleSubmit} className="flex gap-4 relative z-10">
        <div className="flex-1 relative">
          <input
            type="text"
            placeholder={isListening ? "Listening... speak now" : "Type your question about the documents..."}
            className="w-full px-5 py-4 border border-gray-200 dark:border-gray-600 rounded-2xl text-sm bg-white/90 dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-300 hover:shadow-xl placeholder-gray-400 dark:placeholder-gray-500 backdrop-blur-[1px]"
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {(text || isListening) && (
            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
              <div className={`w-2 h-2 ${isListening ? 'bg-red-500' : 'bg-green-500'} rounded-full animate-pulse`}></div>
            </div>
          )}
        </div>

        {/* Mic button */}
        <button
          type="button"
          onClick={() => (isListening ? stopListening() : startListening())}
          disabled={!isSpeechSupported}
          className={`px-4 py-4 rounded-2xl text-sm font-medium transition-all duration-300 shadow-lg simple-hover-lift border ${
            isSpeechSupported
              ? isListening
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700'
              : 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-gray-300 dark:border-gray-600'
          }`}
          title={isSpeechSupported ? (isListening ? 'Stop recording' : 'Record voice') : 'Voice input not supported in this browser'}
        >
          <div className="flex items-center gap-2">
            {isListening ? <FiMicOff className="w-4 h-4"/> : <FiMic className="w-4 h-4"/>}
            <span className="hidden sm:inline">{isListening ? 'Stop' : 'Voice'}</span>
          </div>
        </button>
        
        {/* Send/Cancel button - dynamically switches based on streaming state */}
        {isStreaming ? (
          <button
            type="button"
            onClick={onCancelStream}
            className="px-6 py-4 rounded-2xl text-sm font-medium transition-all duration-300 shadow-lg simple-hover-lift bg-gradient-to-r from-red-500 to-red-600 text-white hover:from-red-600 hover:to-red-700 shadow-red-500/25"
          >
            <div className="flex items-center gap-2">
              <FiX className="w-4 h-4" />
              <span>Cancel</span>
            </div>
          </button>
        ) : (
          <button
            type="submit"
            disabled={!text.trim()}
            className={`px-6 py-4 rounded-2xl text-sm font-medium transition-all duration-300 shadow-lg simple-hover-lift ${
              text.trim()
                ? "bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 shadow-indigo-500/25"
                : "bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed"
            }`}
          >
            <div className="flex items-center gap-2">
              <span>Send</span>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </div>
          </button>
        )}
      </form>
    </div>
  );
}
